/**
 * KnowledgeServiceDetail — vibe-designer 1.170.0 Phase 3.
 *
 * Per-service detail panel. Shows availability badge, primary count +
 * label, optional secondary breakdown (e.g., BRAIN's per-collection
 * counts), honest error message, AND a Phase 3 search input that calls
 * `GET /api/knowledge/services/{name}/search` when the service is
 * available.
 *
 * Honest-failure contract preserved across all states:
 *   - probe False → no search input rendered (service unavailable)
 *   - probe True + COGNITION auth gap → search returns available=true,
 *     hits=[], error="cognee /api/v1/search requires auth token (401)…"
 *     SPA renders an error banner (NOT a silent empty list)
 *   - probe True + empty hits → "No results" empty state
 *   - probe True + hits → result card list
 */
import { useState, type FormEvent } from 'react';
import clsx from 'clsx';
import { Loader2 } from 'lucide-react';
import { useKnowledgeStore } from '@/stores/knowledgeStore';
import { ObsidianPageList } from './ObsidianPageList';
import { ObsidianGraphView } from './ObsidianGraphView';
import { MindIngestStats } from './MindIngestStats';
import { MindGraphView } from './MindGraphView';
import { CortexGraphView } from './CortexGraphView';
import { SightImageGrid } from './SightImageGrid';
import { PillarChips } from './PillarChips';
import { KnowledgeWebSearchDetail } from './KnowledgeWebSearchDetail';
import { KnowledgeSynapseDetail } from './KnowledgeSynapseDetail';
import type { KnowledgeServiceStats, KnowledgeSearchHit } from '@/services/knowledgeApi';

const SERVICE_LABELS: Record<KnowledgeServiceStats['service'], string> = {
  mind: 'THE MIND',
  cortex: 'THE CORTEX',
  brain: 'THE BRAIN',
  sight: 'THE SIGHT',
  cognition: 'THE COGNITION',
  obsidian: 'OBSIDIAN',
};

interface KnowledgeServiceDetailProps {
  // 1.185.0 Phase 7.2: widened to include 'web_search' (R1 C1).
  // web_search short-circuits to KnowledgeWebSearchDetail BEFORE any
  // searchByService[service] read, so the rest of this component stays
  // typed against the 6-corpus union.
  // 2026-07-02: same treatment for 'synapse' (the knowledge loop).
  service: KnowledgeServiceStats['service'] | 'web_search' | 'synapse';
}

export function KnowledgeServiceDetail({ service }: KnowledgeServiceDetailProps) {
  // Phase 7.2 — hooks must be called unconditionally per React rules-of-hooks.
  // For web_search we still call all the same selectors (with a safe default
  // 'mind' key) — the values are ignored because we render
  // KnowledgeWebSearchDetail and return immediately AFTER the hook calls.
  const corpusKey: KnowledgeServiceStats['service'] =
    service === 'web_search' || service === 'synapse' ? 'mind' : service;
  const overview = useKnowledgeStore((s) => s.overview);
  const isLoading = useKnowledgeStore((s) => s.isLoading);
  const searchResponse = useKnowledgeStore((s) => s.searchByService[corpusKey]);
  const isSearching = useKnowledgeStore((s) => s.isSearchingByService[corpusKey]);
  const searchError = useKnowledgeStore((s) => s.searchErrorByService[corpusKey]);
  const runSearch = useKnowledgeStore((s) => s.runSearch);

  const [draft, setDraft] = useState('');
  // 1.184.0 Phase 7.1 — per-component pillar state (not localStorage; chips
  // are per-query). Only MIND enforces the filter; on other services the
  // chips render disabled with a MIND-only tooltip.
  const [pillar, setPillar] = useState<string | null>(null);

  // Phase 7.2 — web_search is NOT a corpus; short-circuit AFTER all hooks
  // are called (React rules-of-hooks) but BEFORE we read overview/svc which
  // doesn't include web_search.
  if (service === 'web_search') {
    return <KnowledgeWebSearchDetail />;
  }
  // 2026-07-02 — SYNAPSE is the knowledge loop, not a corpus; its panel reads
  // its own status route (same post-hooks short-circuit rationale as above).
  if (service === 'synapse') {
    return <KnowledgeSynapseDetail />;
  }

  if (overview === null) {
    return (
      <div
        data-testid="knowledge-service-detail-loading"
        className="flex-1 flex items-center justify-center text-vibe-text-secondary text-sm p-8"
      >
        {isLoading ? 'Loading…' : 'Overview not loaded yet.'}
      </div>
    );
  }

  const svc = overview.find((s) => s.service === service);
  if (!svc) {
    return (
      <div
        data-testid="knowledge-service-detail-not-found"
        className="flex-1 p-8 text-vibe-text-secondary"
      >
        Service <code className="font-mono">{service}</code> not in overview response.
      </div>
    );
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = draft.trim();
    if (!q) return;
    // Only forward pillar_filter for MIND — others accept the kwarg as a
    // Protocol no-op but the chips render disabled in their UI.
    const opts = service === 'mind' && pillar ? { pillar_filter: pillar } : undefined;
    void runSearch(service, q, 10, opts);
  };

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <header className="mb-4 flex items-center justify-between">
        <h1 className="text-lg font-semibold text-vibe-text">
          {SERVICE_LABELS[service]}
        </h1>
        <span
          className={clsx(
            'px-2 py-0.5 text-xs rounded',
            svc.available
              ? 'bg-vibe-success/20 text-vibe-success'
              : 'bg-vibe-text-secondary/20 text-vibe-text-secondary',
          )}
        >
          {svc.available ? 'Available' : 'Unavailable'}
        </span>
      </header>

      <div className="mb-6">
        <div
          data-testid="knowledge-service-count"
          className="text-3xl font-semibold text-vibe-text tabular-nums"
        >
          {svc.count === null ? '—' : svc.count}
        </div>
        {svc.label && (
          <div className="text-sm text-vibe-text-secondary mt-1">{svc.label}</div>
        )}
      </div>

      {svc.secondary && Object.keys(svc.secondary).length > 0 && (
        <div className="mb-6">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-vibe-text-secondary mb-2">
            By collection
          </h2>
          <ul className="space-y-1">
            {Object.entries(svc.secondary).map(([k, v]) => (
              <li
                key={k}
                className="flex items-center justify-between border-b border-vibe-border py-1.5 text-sm"
              >
                <span className="font-mono text-vibe-text-secondary">{k}</span>
                <span className="tabular-nums text-vibe-text">{v}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {svc.error && (
        <div className="rounded border border-vibe-warning/30 bg-vibe-warning/10 p-3 text-sm text-vibe-warning mb-6">
          {svc.error}
        </div>
      )}

      {/* Phase 6.4-B BUG-BRAIN-1 LOCK — when a service is unavailable, give
          the user a path to retry without navigating away. Service-specific
          help text names the concrete unblock action where applicable. */}
      {!svc.available && (
        <div className="flex items-center gap-3 mb-6">
          <button
            data-testid="knowledge-service-retry"
            type="button"
            onClick={() => void useKnowledgeStore.getState().fetchOverview()}
            className="px-3 py-1.5 text-xs rounded bg-vibe-bg-tertiary text-vibe-text-secondary hover:bg-vibe-border"
          >
            Retry probe
          </button>
          {service === 'brain' && (
            <span
              data-testid="knowledge-service-help-brain"
              className="text-xs text-vibe-text-secondary"
            >
              BRAIN requires ChromaDB + Ollama (DGX1:9100). Start Ollama on DGX1
              or pivot to Infinity embeddings to re-enable.
            </span>
          )}
          {service === 'cognition' && (
            <span
              data-testid="knowledge-service-help-cognition"
              className="text-xs text-vibe-text-secondary"
            >
              cognee requires an auth token. The vibe-backend integration with
              cognee is pending the COGNITION auth workstream — once wired,
              re-enable here.
            </span>
          )}
        </div>
      )}

      {/* Phase 5β1+5β2: per-service viz section above the search input.
          OBSIDIAN page list (5β1); MIND chunks-per-day + per-table breakdown
          (5β2); CORTEX force-directed entity graph (5β2). All three use the
          same `service === '...' && svc.available && <Viz/>` mount pattern. */}
      {service === 'obsidian' && svc.available && <ObsidianPageList />}
      {service === 'obsidian' && svc.available && <ObsidianGraphView />}
      {service === 'mind' && svc.available && <MindIngestStats />}
      {service === 'mind' && svc.available && <MindGraphView />}
      {service === 'cortex' && svc.available && <CortexGraphView />}
      {service === 'sight' && svc.available && <SightImageGrid />}

      {svc.available && (
        <section className="mt-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-vibe-text-secondary mb-2">
            Search
          </h2>
          <PillarChips
            value={pillar}
            onChange={setPillar}
            disabled={service !== 'mind'}
            className="mb-3"
          />
          <form onSubmit={onSubmit} className="flex gap-2 mb-3">
            <input
              data-testid="knowledge-search-input"
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={`Search ${SERVICE_LABELS[service]}…`}
              className="flex-1 px-3 py-1.5 text-sm rounded bg-vibe-bg-secondary border border-vibe-border text-vibe-text placeholder:text-vibe-text-secondary"
              disabled={isSearching}
            />
            <button
              data-testid="knowledge-search-submit"
              type="submit"
              disabled={isSearching || draft.trim().length === 0}
              className="px-3 py-1.5 text-sm rounded bg-vibe-accent text-vibe-bg disabled:opacity-50"
            >
              {isSearching ? 'Searching…' : 'Search'}
            </button>
          </form>

          {isSearching && (
            <div
              data-testid="knowledge-search-loading"
              className="flex items-center gap-2 text-sm text-vibe-text-secondary py-4"
            >
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Searching {SERVICE_LABELS[service]}…</span>
            </div>
          )}

          {!isSearching && searchError && (
            <div
              data-testid="knowledge-search-network-error"
              className="rounded border border-vibe-error/30 bg-vibe-error/10 p-3 text-sm text-vibe-error"
            >
              Network error: {searchError.message}
            </div>
          )}

          {!isSearching && searchResponse && searchResponse.error && (
            <div
              data-testid="knowledge-search-error"
              className="rounded border border-vibe-warning/30 bg-vibe-warning/10 p-3 text-sm text-vibe-warning"
            >
              {searchResponse.error}
            </div>
          )}

          {!isSearching &&
            searchResponse &&
            !searchResponse.error &&
            searchResponse.hits.length === 0 && (
              <div
                data-testid="knowledge-search-empty"
                className="text-sm text-vibe-text-secondary py-4"
              >
                No results for «{searchResponse.query}».
              </div>
            )}

          {!isSearching &&
            searchResponse &&
            searchResponse.hits.length > 0 && (
              <ul className="space-y-2">
                {searchResponse.hits.map((hit, i) => (
                  <KnowledgeSearchResultCard
                    key={`${service}-${i}`}
                    service={service}
                    index={i}
                    hit={hit}
                  />
                ))}
              </ul>
            )}
        </section>
      )}
    </div>
  );
}

interface ResultCardProps {
  service: KnowledgeServiceStats['service'];
  index: number;
  hit: KnowledgeSearchHit;
}

function KnowledgeSearchResultCard({ service, index, hit }: ResultCardProps) {
  return (
    <li
      data-testid={`knowledge-search-result-${service}-${index}`}
      className="rounded border border-vibe-border bg-vibe-bg-secondary p-3"
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="font-medium text-sm text-vibe-text truncate">
          {hit.title}
        </span>
        {hit.score !== null && (
          <span className="text-xs tabular-nums text-vibe-text-secondary">
            {hit.score.toFixed(3)}
          </span>
        )}
      </div>
      <div className="text-sm text-vibe-text-secondary whitespace-pre-wrap break-words max-h-32 overflow-hidden">
        {hit.snippet}
      </div>
      {hit.source && hit.source !== hit.title && (
        <div className="text-xs font-mono text-vibe-text-secondary mt-1.5">
          {hit.source}
        </div>
      )}
    </li>
  );
}
